import React, { useState, useEffect, useCallback } from 'react';
import { FileText, RefreshCw, Search, AlertTriangle, Info, AlertCircle, CheckCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function safeFetchJson(path, options = {}) {
  const url = `${BACKEND_URL}${path}`;
  const token =
    localStorage.getItem('superadmin_session') ||
    localStorage.getItem('superadmin_session_token') ||
    localStorage.getItem('superAdminToken') ||
    localStorage.getItem('superadminToken');

  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(url, { credentials: 'include', ...options, headers }).then((res) => {
    if (!res.ok) return res.text().then((t) => { throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`); });
    return res.json();
  });
}

const SEVERITY_CONFIG = {
  info:     { icon: Info,          color: 'bg-blue-100 text-blue-800',   row: '' },
  warning:  { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-800', row: 'bg-yellow-50' },
  error:    { icon: AlertCircle,   color: 'bg-red-100 text-red-800',     row: 'bg-red-50' },
  critical: { icon: AlertCircle,   color: 'bg-red-200 text-red-900',     row: 'bg-red-100' },
  success:  { icon: CheckCircle,   color: 'bg-green-100 text-green-800', row: '' },
};

function formatTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('all');

  const fetchLogs = useCallback(async (sev) => {
    setLoading(true);
    setError(null);
    try {
      const qs = sev && sev !== 'all' ? `?severity=${sev}&limit=200` : '?limit=200';
      const data = await safeFetchJson(`/api/v1/super-admin/audit-logs${qs}`);
      setLogs(data.audit_logs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(severity); }, [fetchLogs, severity]);

  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();
    return !q ||
      (l.action || '').toLowerCase().includes(q) ||
      (l.resource_type || '').toLowerCase().includes(q) ||
      (l.resource_id || '').toLowerCase().includes(q) ||
      (l.user_id || '').toLowerCase().includes(q);
  });

  const counts = { info: 0, warning: 0, error: 0, critical: 0 };
  logs.forEach((l) => { if (counts[l.severity] !== undefined) counts[l.severity]++; });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600 mt-1">Security and activity logging</p>
        </div>
        <Button onClick={() => fetchLogs(severity)} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(counts).map(([sev, count]) => {
          const cfg = SEVERITY_CONFIG[sev] || {};
          const Icon = cfg.icon || Info;
          return (
            <div key={sev} className="bg-white rounded-xl shadow-sm p-4 border">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-gray-400" />
                <span className="text-xs text-gray-500 capitalize">{sev}</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search action, resource, user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-orange-400" />
            Loading audit logs...
          </div>
        )}
        {error && !loading && (
          <div className="p-8 text-center">
            <p className="text-red-600 font-medium">{error}</p>
            <Button onClick={() => fetchLogs(severity)} className="mt-4" variant="outline">Retry</Button>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{search ? 'No logs match your search.' : 'No audit logs found.'}</p>
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Resource</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((log, idx) => {
                const cfg = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                return (
                  <tr key={log.id || log._id || idx} className={`hover:bg-gray-50 transition-colors ${cfg.row}`}>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatTime(log.timestamp)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{log.action || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{log.resource_type || '—'}</div>
                      {log.resource_id && <div className="text-xs text-gray-400 truncate max-w-[120px]">{log.resource_id}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{log.user_id || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge className={cfg.color}>{log.severity || 'info'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{log.ip_address || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
